/**
 * Which stylist looks after which member.
 *
 * A member has at most one stylist (`users.assigned_stylist_id`), and that
 * stylist is responsible for the member's styling: their requests, their chats
 * and the outfits waiting for review. This screen is where staff move members
 * between stylists, or give a stylist to members who have none. The same
 * member list and the same change dialog are reused on a stylist's own page.
 *
 * The write is the audited member `PATCH`: a stylist id assigns, an explicit
 * `null` unassigns. Moving a member's open work to the new stylist is the
 * server's job, not this screen's.
 *
 * Reads the server may add later - a stylist's members, and a bulk assign -
 * are looked up in the generated client by name and used when present. Until
 * then the whole member list is paged in and grouped here, and a bulk assign
 * is one `PATCH` per member, in order, with each result shown. The member read
 * also falls back when it fails; the bulk assign falls back only when the
 * server says it has no such route, since a failed one may have been applied.
 */

import * as api from '@closet-theory/api';
import type { ConsumerPage, ConsumerSummary, StylistSummary } from '@closet-theory/api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@ui/components/base/buttons/button';
import { Callout, FailureText, Section, StatusBadge } from '@ui/console/primitives';
import { ActionDialog } from '@ui/console/PageShell';
import { SelectField } from '@ui/console/forms';
import { Bar, Quantity, formatCount, plural } from '@ui/console/measures';
import { MetricBand } from '@ui/console/MetricBand';
import { configureAdminApi, handleUnauthorized } from './auth';
import { errorDetail } from './operation-policy';
import { Actions, Page, State, Status } from './components';
import { RecordTable } from './record-table';
import { EntityRef } from './references';

/* ------------------------------------------------------------------ reads */

const PAGE_SIZE = 100;
/** A ceiling on paging, so a server that never reports its total cannot loop us forever. */
const MAX_ROWS = 20_000;

export const ASSIGNMENT_REASON = 'Changed a member’s stylist from the admin console.';

type SdkResult = { data?: unknown; error?: unknown; response: Response };
type Operation = (options: Record<string, unknown>) => Promise<SdkResult>;

/** Reads and writes the generated client may gain; matched by the route they call. */
export const FUTURE_OPERATIONS = {
  stylistMembers: /ApiV1InternalStylists(?:Id|StylistId)MembersGet$/,
  assignStylistMembers: /ApiV1InternalStylists(?:Id|StylistId)MembersPost$/,
} as const;

/** The generated operation whose name matches, if this build of the client has one. */
export function findOperation(pattern: RegExp): Operation | undefined {
  const exports = api as unknown as Record<string, unknown>;
  for (const [name, value] of Object.entries(exports)) {
    if (pattern.test(name) && typeof value === 'function') return value as Operation;
  }
  return undefined;
}

export class ForbiddenRead extends Error {
  constructor(subject: string) {
    super(`Your role cannot see ${subject}.`);
  }
}

function status(result: { response?: Response | undefined }): number {
  return result.response?.status ?? 0;
}

/** Every page of the member list, with whatever server-side filters apply. */
export async function readAllMembers(
  filters: Omit<
    NonNullable<api.ListMembersApiV1InternalMembersGetData['query']>,
    'limit' | 'offset'
  > = {},
): Promise<ConsumerSummary[]> {
  configureAdminApi();
  const items: ConsumerSummary[] = [];
  for (let offset = 0; offset < MAX_ROWS; offset += PAGE_SIZE) {
    const result = await api.listMembersApiV1InternalMembersGet({
      query: { ...filters, limit: PAGE_SIZE, offset },
    });
    handleUnauthorized(status(result));
    if (status(result) === 403) throw new ForbiddenRead('the member list');
    if (result.error || !result.data) throw new Error('The member list could not be loaded.');
    items.push(...result.data.items);
    if (!result.data.items.length || items.length >= result.data.total) break;
  }
  return items;
}

/** Every active stylist: the only ones a member can be given. */
export async function readActiveStylists(): Promise<StylistSummary[]> {
  configureAdminApi();
  const items: StylistSummary[] = [];
  for (let offset = 0; offset < MAX_ROWS; offset += PAGE_SIZE) {
    const result = await api.listStylistsApiV1InternalStylistsGet({
      query: { status: 'active', limit: PAGE_SIZE, offset },
    });
    handleUnauthorized(status(result));
    if (status(result) === 403) throw new ForbiddenRead('the stylist list');
    if (result.error || !result.data) throw new Error('The stylist list could not be loaded.');
    items.push(...result.data.items);
    if (!result.data.items.length || items.length >= result.data.total) break;
  }
  return items;
}

function pageItems(data: unknown): { items: ConsumerSummary[]; total?: number } {
  if (Array.isArray(data)) return { items: data as ConsumerSummary[] };
  if (data && typeof data === 'object' && Array.isArray((data as ConsumerPage).items)) {
    const page = data as Partial<ConsumerPage> & { items: ConsumerSummary[] };
    return { items: page.items, ...(typeof page.total === 'number' ? { total: page.total } : {}) };
  }
  return { items: [] };
}

/**
 * One stylist's members. Uses the stylist's own member read when the client
 * has it and it answers; otherwise reads every member and keeps that stylist's.
 */
export async function readStylistMembers(stylistId: string): Promise<ConsumerSummary[]> {
  const read = findOperation(FUTURE_OPERATIONS.stylistMembers);
  const direct = read ? await readStylistMembersFrom(read, stylistId) : undefined;
  if (direct) return direct;
  const everyone = await readAllMembers({ assignment: 'assigned' });
  return everyone.filter((member) => member.assigned_stylist_id === stylistId);
}

/** Statuses that mean the stylist's own member read is not usable on this server. */
function readUnavailable(code: number): boolean {
  return code === 404 || code === 405 || code === 501 || code >= 500;
}

/**
 * The stylist's members from their own read, or `undefined` when that read is
 * not there or not working, so the caller groups the whole list instead.
 */
async function readStylistMembersFrom(
  read: Operation,
  stylistId: string,
): Promise<ConsumerSummary[] | undefined> {
  configureAdminApi();
  const items: ConsumerSummary[] = [];
  for (let offset = 0; offset < MAX_ROWS; offset += PAGE_SIZE) {
    let result: SdkResult;
    try {
      result = await read({
        path: { id: stylistId, stylist_id: stylistId },
        query: { limit: PAGE_SIZE, offset },
      });
    } catch {
      return undefined;
    }
    handleUnauthorized(status(result));
    if (status(result) === 403) throw new ForbiddenRead('this stylist’s members');
    if (readUnavailable(status(result))) return undefined;
    if (result.error || result.data === undefined)
      throw new Error('This stylist’s members could not be loaded.');
    const page = pageItems(result.data);
    items.push(...page.items);
    if (page.total === undefined || !page.items.length || items.length >= page.total) break;
  }
  return items;
}

/* ----------------------------------------------------------------- writes */

export type AssignOutcome = { ok: true } | { ok: false; message: string };

function errorCode(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null || !('detail' in error)) return undefined;
  const { detail } = error;
  if (typeof detail === 'object' && detail !== null && 'code' in detail) {
    const code = detail.code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

/** The sentence staff see when the server refuses an assignment. */
export function assignmentFailure(
  result: { error?: unknown; response?: Response | undefined },
  stylistName: string | undefined,
): string {
  const code = status(result);
  if (code === 403) return 'Your role cannot change who looks after a member. Nothing was changed.';
  if (code === 404) return 'This member no longer exists. Refresh the page. Nothing was changed.';
  if (errorCode(result.error) === 'stylist_not_assignable')
    return `${stylistName ?? 'That stylist'} cannot take members: they are not an active stylist any more. Nothing was changed. Refresh the page and pick someone else.`;
  return errorDetail(
    result.error,
    `The server did not accept the change${code ? ` (error ${code})` : ''}. Nothing was changed.`,
  );
}

/** Give a member to a stylist, or take their stylist away with `null`. */
export async function assignMember(
  memberId: string,
  stylist: { id: string; name: string } | null,
): Promise<AssignOutcome> {
  configureAdminApi();
  const result = await api.updateUserApiV1InternalUsersIdPatch({
    path: { id: memberId },
    body: { assigned_stylist_id: stylist ? stylist.id : null, reason: ASSIGNMENT_REASON },
  });
  handleUnauthorized(status(result));
  if (result.error || status(result) >= 400)
    return { ok: false, message: assignmentFailure(result, stylist?.name) };
  return { ok: true };
}

export type BulkResult = { member: ConsumerSummary; outcome: AssignOutcome };
export type BulkProgress = { done: number; total: number; results: BulkResult[] };

/** Statuses that mean this server has no bulk assign, so one `PATCH` each is safe. */
function bulkUnavailable(code: number): boolean {
  return code === 404 || code === 405 || code === 501;
}

/**
 * The bulk assign's outcome for every member, or `undefined` when the server
 * has no bulk assign. Any other failure is reported, never retried member by
 * member: the request may already have been applied.
 */
async function assignInOneRequest(
  bulk: Operation,
  members: ConsumerSummary[],
  stylist: { id: string; name: string },
): Promise<AssignOutcome | undefined> {
  configureAdminApi();
  let result: SdkResult;
  try {
    result = await bulk({
      path: { id: stylist.id, stylist_id: stylist.id },
      body: { member_ids: members.map((member) => member.id), reason: ASSIGNMENT_REASON },
    });
  } catch {
    return { ok: false, message: bulkUnconfirmed() };
  }
  const code = status(result);
  handleUnauthorized(code);
  if (bulkUnavailable(code)) return undefined;
  if (code >= 500) return { ok: false, message: bulkUnconfirmed(code) };
  if (result.error || code >= 400)
    return { ok: false, message: assignmentFailure(result, stylist.name) };
  return { ok: true };
}

/** A bulk assign that may or may not have been applied. */
export function bulkUnconfirmed(code?: number): string {
  return `The server did not confirm the assignment${code ? ` (error ${code})` : ''}. Refresh the page to see who was moved before trying again.`;
}

/**
 * Give several members to one stylist. One request when the server offers a
 * bulk assign; otherwise one `PATCH` each, in order, reporting after each.
 */
export async function assignMany(
  members: ConsumerSummary[],
  stylist: { id: string; name: string },
  onProgress: (progress: BulkProgress) => void,
): Promise<BulkResult[]> {
  const bulk = findOperation(FUTURE_OPERATIONS.assignStylistMembers);
  if (bulk) {
    const outcome = await assignInOneRequest(bulk, members, stylist);
    if (outcome) {
      const results = members.map((member) => ({ member, outcome }));
      onProgress({ done: members.length, total: members.length, results });
      return results;
    }
  }
  const results: BulkResult[] = [];
  onProgress({ done: 0, total: members.length, results: [] });
  for (const member of members) {
    let outcome: AssignOutcome;
    try {
      outcome = await assignMember(member.id, stylist);
    } catch {
      outcome = {
        ok: false,
        message: 'The request did not reach the server. Nothing was changed.',
      };
    }
    results.push({ member, outcome });
    onProgress({ done: results.length, total: members.length, results: [...results] });
  }
  return results;
}

/* ---------------------------------------------------------------- grouping */

export type StylistGroup = {
  stylist: { id: string; name: string; status: string; capacity_per_day?: number };
  active: boolean;
  members: ConsumerSummary[];
};

/**
 * One group per active stylist (even one with nobody yet), then one per
 * inactive stylist who still has members, then the members nobody has.
 */
export function groupByStylist(
  members: readonly ConsumerSummary[],
  stylists: readonly StylistSummary[],
): { groups: StylistGroup[]; unassigned: ConsumerSummary[] } {
  const groups = new Map<string, StylistGroup>();
  for (const stylist of stylists)
    groups.set(stylist.id, {
      stylist: {
        id: stylist.id,
        name: stylist.name,
        status: stylist.status,
        capacity_per_day: stylist.capacity_per_day,
      },
      active: true,
      members: [],
    });
  const unassigned: ConsumerSummary[] = [];
  for (const member of members) {
    const id = member.assigned_stylist_id;
    if (!id) {
      unassigned.push(member);
      continue;
    }
    let group = groups.get(id);
    if (!group) {
      group = {
        stylist: { id, name: member.assigned_stylist_name ?? 'Stylist', status: 'not active' },
        active: false,
        members: [],
      };
      groups.set(id, group);
    }
    group.members.push(member);
  }
  const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
  for (const group of groups.values()) group.members.sort(byName);
  unassigned.sort(byName);
  const ordered = [...groups.values()].sort(
    (a, b) => Number(b.active) - Number(a.active) || byName(a.stylist, b.stylist),
  );
  return { groups: ordered, unassigned };
}

function matches(member: ConsumerSummary, needle: string): boolean {
  if (!needle) return true;
  return [member.name, member.email ?? ''].some((value) => value.toLowerCase().includes(needle));
}

/* ------------------------------------------------------------------ dialog */

const UNASSIGN = 'none';

function stylistOption(stylist: StylistSummary, carrying: number | undefined) {
  const count = carrying ?? stylist.assigned_members;
  return {
    value: stylist.id,
    label: `${stylist.name} — ${count} ${plural(count, 'member')} assigned, daily capacity ${stylist.capacity_per_day} (not enforced)`,
  };
}

/** What a move does, said before it is confirmed. */
export function assignmentConsequence(
  memberName: string,
  to: string | undefined,
  count = 1,
): string {
  const who = count === 1 ? memberName : `these ${count} members`;
  const whose = count === 1 ? `${memberName}’s` : 'their';
  if (!to)
    return `${memberName} will have no stylist. Their daily outfits still go out, but nobody reviews them first and nobody answers their chats until you assign a stylist again. Their wardrobe and history are not touched.`;
  return `Open requests, chats and pending outfit reviews move to ${to}. ${to} looks after ${whose} styling from now on, and ${who} ${count === 1 ? 'appears' : 'appear'} in ${to}’s list straight away.`;
}

export function ChangeStylistDialog({
  member,
  stylists,
  carrying,
  onClose,
  onChanged,
}: {
  member: ConsumerSummary;
  stylists: readonly StylistSummary[];
  /** Members each stylist looks after, as this page counts them. */
  carrying?: ReadonlyMap<string, number>;
  onClose: () => void;
  onChanged: (summary: string) => void | Promise<void>;
}) {
  const [choice, setChoice] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string>();
  const current = member.assigned_stylist_id;
  const chosen = stylists.find((stylist) => stylist.id === choice);
  const unassigning = choice === UNASSIGN;

  const confirm = async () => {
    if (!chosen && !unassigning) return;
    setBusy(true);
    setFailure(undefined);
    let outcome: AssignOutcome;
    try {
      outcome = await assignMember(member.id, chosen ? { id: chosen.id, name: chosen.name } : null);
    } catch {
      outcome = {
        ok: false,
        message: 'The request did not reach the server. Nothing was changed.',
      };
    }
    setBusy(false);
    if (!outcome.ok) {
      setFailure(outcome.message);
      return;
    }
    await onChanged(
      chosen
        ? `${member.name} is now looked after by ${chosen.name}.`
        : `${member.name} no longer has a stylist.`,
    );
  };

  return (
    <ActionDialog
      title={`Change ${member.name}’s stylist`}
      description={
        current
          ? `Looked after by ${member.assigned_stylist_name ?? 'a stylist'} now.`
          : 'Nobody looks after this member now.'
      }
      onClose={onClose}
    >
      <div className="flex flex-col gap-4">
        <SelectField
          label="New stylist"
          value={choice}
          disabled={busy}
          onChange={(event) => {
            setChoice(event.currentTarget.value);
            setFailure(undefined);
          }}
          hint="Only active stylists can be picked."
          options={[
            { value: '', label: 'Choose a stylist' },
            ...stylists
              .filter((stylist) => stylist.id !== current)
              .map((stylist) => stylistOption(stylist, carrying?.get(stylist.id))),
            ...(current ? [{ value: UNASSIGN, label: 'No stylist — remove their stylist' }] : []),
          ]}
        />
        {chosen || unassigning ? (
          <Callout
            tone="warning"
            title={
              chosen ? `Move ${member.name} to ${chosen.name}?` : `Remove ${member.name}’s stylist?`
            }
          >
            <span className="block">{assignmentConsequence(member.name, chosen?.name)}</span>
            <span className="block pt-1">This is recorded against your staff account.</span>
          </Callout>
        ) : null}
        {failure ? (
          <Callout tone="error" title="The stylist was not changed.">
            <FailureText raw={failure} />
          </Callout>
        ) : null}
        <Actions>
          <Button size="md" color="secondary" isDisabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="md"
            color="primary"
            isDisabled={busy || (!chosen && !unassigning)}
            onClick={() => void confirm()}
          >
            {busy
              ? 'Saving…'
              : chosen
                ? `Move to ${chosen.name}`
                : unassigning
                  ? 'Remove stylist'
                  : 'Confirm'}
          </Button>
        </Actions>
      </div>
    </ActionDialog>
  );
}

/* ----------------------------------------------------------- member table */

/**
 * A list of members with a "Change stylist" button on each row. Used for each
 * stylist on the assignments page and on a stylist's own page.
 */
export function MemberAssignmentTable({
  members,
  stylists,
  carrying,
  onChanged,
  selection,
  emptyHint,
  label,
}: {
  members: readonly ConsumerSummary[];
  stylists: readonly StylistSummary[];
  carrying?: ReadonlyMap<string, number>;
  onChanged: (summary: string) => void | Promise<void>;
  selection?: { selected: readonly string[]; onChange: (next: string[]) => void };
  emptyHint: string;
  /** What these members are, for the table's own nouns. */
  label: string;
}) {
  const [changing, setChanging] = useState<ConsumerSummary>();
  if (!members.length)
    return (
      <p className="rounded-xl border border-secondary bg-primary p-4 text-sm text-tertiary">
        {emptyHint}
      </p>
    );
  return (
    <>
      <RecordTable
        noun={label}
        nounSingular="member"
        searchable={false}
        rowKind="member"
        records={members}
        columns={['id', 'status', 'access_status']}
        columnLabel={(column) =>
          column === 'status' ? 'Status' : column === 'access_status' ? 'Access' : undefined
        }
        renderCell={(column, record) => {
          const value = record[column];
          if (column === 'status' && typeof value === 'string') return <Status value={value} />;
          if (column === 'access_status')
            return value === 'waitlisted' ? <Status value="waitlisted" /> : <span>In the app</span>;
          return undefined;
        }}
        {...(selection
          ? {
              selection: {
                ...selection,
                idFor: (record: Record<string, unknown>) => String(record['id']),
                itemNoun: 'member',
              },
            }
          : {})}
        rowActions={(record) => {
          const member = record as unknown as ConsumerSummary;
          return (
            <Button
              size="sm"
              color="secondary"
              aria-label={`Change stylist for ${member.name}`}
              onClick={() => setChanging(member)}
            >
              Change stylist
            </Button>
          );
        }}
      />
      {changing ? (
        <ChangeStylistDialog
          member={changing}
          stylists={stylists}
          {...(carrying ? { carrying } : {})}
          onClose={() => setChanging(undefined)}
          onChanged={async (summary) => {
            setChanging(undefined);
            await onChanged(summary);
          }}
        />
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------- bulk assign */

function BulkAssignDialog({
  members,
  stylist,
  onClose,
  onFinished,
}: {
  members: ConsumerSummary[];
  stylist: StylistSummary;
  onClose: () => void;
  onFinished: () => void | Promise<void>;
}) {
  const [progress, setProgress] = useState<BulkProgress>();
  const running = Boolean(progress && progress.done < progress.total);
  const finished = Boolean(progress && progress.done === progress.total);
  const failed = progress?.results.filter((result) => !result.outcome.ok) ?? [];

  const start = async () => {
    await assignMany(members, { id: stylist.id, name: stylist.name }, setProgress);
  };
  const close = () => {
    if (running) return;
    if (finished) void onFinished();
    onClose();
  };

  return (
    <ActionDialog
      title={`Assign ${members.length} ${plural(members.length, 'member')} to ${stylist.name}`}
      description={assignmentConsequence(members[0]?.name ?? '', stylist.name, members.length)}
      onClose={close}
      width="lg"
    >
      <div className="flex flex-col gap-4">
        {progress ? (
          <div className="flex flex-col gap-2">
            <p aria-live="polite" className="text-sm font-medium text-secondary">
              {finished
                ? failed.length
                  ? `Done. ${progress.total - failed.length} of ${progress.total} moved; ${failed.length} not moved.`
                  : `Done. All ${progress.total} moved to ${stylist.name}.`
                : `Assigning ${progress.done + 1} of ${progress.total}…`}
            </p>
            <Bar
              fraction={progress.total ? progress.done / progress.total : 0}
              tone={failed.length ? 'warning' : 'success'}
              label={`Assigned ${progress.done} of ${progress.total}`}
            />
            <ul aria-label="Results" className="flex flex-col gap-1">
              {progress.results.map(({ member, outcome }) => (
                <li key={member.id} className="flex flex-wrap items-center gap-2 text-sm">
                  <EntityRef kind="member" id={member.id} label={member.name} hideId />
                  {outcome.ok ? (
                    <StatusBadge value="moved" />
                  ) : (
                    <span className="text-error-primary">Not moved: {outcome.message}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <ul aria-label="Members to assign" className="flex flex-col gap-1">
            {members.map((member) => (
              <li key={member.id} className="flex flex-wrap items-center gap-2 text-sm">
                <EntityRef kind="member" id={member.id} label={member.name} hideId />
                <span className="text-tertiary">
                  {member.assigned_stylist_name
                    ? `from ${member.assigned_stylist_name}`
                    : 'no stylist now'}
                </span>
              </li>
            ))}
          </ul>
        )}
        <Actions>
          {finished ? (
            <Button size="md" color="primary" onClick={close}>
              Close
            </Button>
          ) : (
            <>
              <Button size="md" color="secondary" isDisabled={running} onClick={close}>
                Cancel
              </Button>
              <Button size="md" color="primary" isDisabled={running} onClick={() => void start()}>
                {running ? 'Assigning…' : `Assign to ${stylist.name}`}
              </Button>
            </>
          )}
        </Actions>
      </div>
    </ActionDialog>
  );
}

/* -------------------------------------------------------------------- page */

type Loaded = { members: ConsumerSummary[]; stylists: StylistSummary[] };

function useAssignmentData() {
  const [data, setData] = useState<Loaded>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);
  const refresh = useCallback(async (options?: { quiet?: boolean }) => {
    if (!options?.quiet) setLoading(true);
    setError(undefined);
    try {
      const [members, stylists] = await Promise.all([readAllMembers(), readActiveStylists()]);
      setData({ members, stylists });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'This page could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => void refresh(), [refresh]);
  return { data, error, loading, refresh };
}

function StylistCard({
  group,
  stylists,
  carrying,
  selection,
  onChanged,
}: {
  group: StylistGroup;
  stylists: readonly StylistSummary[];
  carrying: ReadonlyMap<string, number>;
  selection: { selected: readonly string[]; onChange: (next: string[]) => void };
  onChanged: (summary: string) => Promise<void>;
}) {
  const { stylist } = group;
  const count = carrying.get(stylist.id) ?? group.members.length;
  const capacity = stylist.capacity_per_day;
  return (
    <section
      aria-label={stylist.name}
      className="flex flex-col gap-3 rounded-xl border border-secondary bg-primary p-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h3 className="text-md font-semibold text-primary">
            <EntityRef kind="stylist" id={stylist.id} label={stylist.name} hideId />
          </h3>
          <Status value={stylist.status} />
        </div>
        <span className="text-sm text-secondary">
          Members assigned:{' '}
          <Quantity value={formatCount(count)} unit={plural(count, 'member')} raw={count} />
          {capacity !== undefined ? (
            <>
              {' · '}Daily capacity <Quantity value={formatCount(capacity)} raw={capacity} /> (not
              enforced)
            </>
          ) : null}
        </span>
      </div>
      {group.active ? null : (
        <Callout tone="warning" title={`${stylist.name} is not an active stylist.`}>
          Their members cannot be looked after by them. Move each one to an active stylist.
        </Callout>
      )}
      <MemberAssignmentTable
        label={`${stylist.name}’s members`}
        members={group.members}
        stylists={stylists}
        carrying={carrying}
        selection={selection}
        onChanged={onChanged}
        emptyHint={`Nobody is assigned to ${stylist.name} yet.`}
      />
    </section>
  );
}

export function StylistAssignments() {
  const source = useAssignmentData();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkTarget, setBulkTarget] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [notice, setNotice] = useState<string>();

  const members = useMemo(() => source.data?.members ?? [], [source.data]);
  const stylists = useMemo(() => source.data?.stylists ?? [], [source.data]);
  const { groups, unassigned } = useMemo(
    () => groupByStylist(members, stylists),
    [members, stylists],
  );
  const carrying = useMemo(
    () => new Map(groups.map((group) => [group.stylist.id, group.members.length])),
    [groups],
  );
  const needle = search.trim().toLowerCase();
  const visibleUnassigned = unassigned.filter((member) => matches(member, needle));
  const visibleGroups = groups
    .map((group) => ({ ...group, members: group.members.filter((m) => matches(m, needle)) }))
    .filter((group) => !needle || group.members.length > 0);
  const hiddenStylists = groups.length - visibleGroups.length;

  // A refresh can take away members the operator had ticked.
  useEffect(() => {
    const present = new Set(members.map((member) => member.id));
    setSelected((current) => {
      const kept = current.filter((id) => present.has(id));
      return kept.length === current.length ? current : kept;
    });
  }, [members]);

  const changed = async (summary: string) => {
    setNotice(summary);
    await source.refresh({ quiet: true });
  };
  const selection = { selected, onChange: setSelected };
  const chosenMembers = members.filter((member) => selected.includes(member.id));
  const target = stylists.find((stylist) => stylist.id === bulkTarget);
  const assigned = members.length - unassigned.length;
  const unassignedActive = unassigned.filter((member) => member.status === 'active').length;

  return (
    <Page
      title="Stylist assignments"
      purpose="Each member has one stylist who looks after their styling. Move members between stylists here, or give a stylist to members who have none."
      band={
        <MetricBand
          label="Who looks after whom"
          figures={[
            source.data
              ? {
                  id: 'stylists',
                  label: 'Active stylists',
                  value: formatCount(stylists.length),
                  unit: plural(stylists.length, 'stylist'),
                  raw: stylists.length,
                }
              : { id: 'stylists', label: 'Active stylists', absent: 'Not loaded yet.' },
            source.data
              ? {
                  id: 'assigned',
                  label: 'Members with a stylist',
                  value: formatCount(assigned),
                  unit: plural(assigned, 'member'),
                  raw: assigned,
                  bar: {
                    fraction: members.length ? assigned / members.length : 0,
                    label: `Members with a stylist: ${assigned} of ${members.length}`,
                    tone: 'success',
                  },
                }
              : { id: 'assigned', label: 'Members with a stylist', absent: 'Not loaded yet.' },
            source.data
              ? {
                  id: 'unassigned',
                  label: 'Members with no stylist',
                  value: formatCount(unassigned.length),
                  unit: plural(unassigned.length, 'member'),
                  raw: unassigned.length,
                  ...(unassigned.length
                    ? { tone: 'warning' as const, statusLabel: 'need a stylist' }
                    : {}),
                  hint: `Every status counted: invited, onboarding, active and paused, waitlisted or not. ${formatCount(unassignedActive)} of them ${unassignedActive === 1 ? 'is' : 'are'} active. Nobody reviews their outfits or answers their chats.`,
                }
              : { id: 'unassigned', label: 'Members with no stylist', absent: 'Not loaded yet.' },
          ]}
        />
      }
      toolbar={{
        search: {
          initial: search,
          placeholder: 'Member name or email',
          onSearch: setSearch,
        },
        onClear: () => setSearch(''),
        canClear: Boolean(search),
        onRefresh: () => void source.refresh(),
        refreshing: source.loading,
      }}
    >
      {notice ? <Callout tone="success" title={notice} /> : null}
      {selected.length ? (
        <div
          role="region"
          aria-label="Assign selected members"
          className="flex flex-wrap items-end gap-3 rounded-xl border border-brand bg-brand-primary p-4"
        >
          <p className="text-sm font-medium text-secondary">
            {selected.length} {plural(selected.length, 'member')} selected
          </p>
          <SelectField
            label="Assign selected members to"
            value={bulkTarget}
            onChange={(event) => setBulkTarget(event.currentTarget.value)}
            options={[
              { value: '', label: 'Choose a stylist' },
              ...stylists.map((stylist) => stylistOption(stylist, carrying.get(stylist.id))),
            ]}
          />
          <Button size="md" color="primary" isDisabled={!target} onClick={() => setBulkOpen(true)}>
            Review assignment
          </Button>
          <Button size="md" color="link-gray" onClick={() => setSelected([])}>
            Clear selection
          </Button>
        </div>
      ) : null}
      <State
        loading={source.loading && !source.data}
        {...(source.error ? { error: source.error } : {})}
        onRetry={() => void source.refresh()}
      >
        <Section
          title="Unassigned members"
          description="Members nobody looks after. Give each one a stylist."
          actions={<StatusBadge value={`${unassigned.length} with no stylist`} />}
        >
          <MemberAssignmentTable
            label="unassigned members"
            members={visibleUnassigned}
            stylists={stylists}
            carrying={carrying}
            selection={selection}
            onChanged={changed}
            emptyHint={
              needle ? 'No unassigned member matches this search.' : 'Every member has a stylist.'
            }
          />
        </Section>
        <Section
          title="Stylists and their members"
          description="Daily capacity is a number set on each stylist. Nothing enforces it, so a stylist can be given more members than it says."
        >
          {needle && hiddenStylists ? (
            <p className="text-sm text-tertiary">
              {hiddenStylists} {plural(hiddenStylists, 'stylist')} with no matching member hidden.
            </p>
          ) : null}
          {visibleGroups.length ? (
            visibleGroups.map((group) => (
              <StylistCard
                key={group.stylist.id}
                group={group}
                stylists={stylists}
                carrying={carrying}
                selection={selection}
                onChanged={changed}
              />
            ))
          ) : (
            <p className="text-sm text-tertiary">
              {needle
                ? 'No stylist has a member who matches this search.'
                : 'There are no active stylists.'}
            </p>
          )}
        </Section>
      </State>
      {bulkOpen && target ? (
        <BulkAssignDialog
          members={chosenMembers}
          stylist={target}
          onClose={() => setBulkOpen(false)}
          onFinished={async () => {
            setSelected([]);
            setNotice(undefined);
            await source.refresh({ quiet: true });
          }}
        />
      ) : null}
    </Page>
  );
}

/* ------------------------------------------------- on a stylist's own page */

/** A stylist's members, with the same change action as the assignments page. */
export function StylistMembers({
  stylistId,
  stylistName,
  onChanged,
}: {
  stylistId: string;
  stylistName: string;
  onChanged?: () => void | Promise<void>;
}) {
  const [data, setData] = useState<Loaded>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<string>();
  const refresh = useCallback(async () => {
    setError(undefined);
    try {
      const [members, stylists] = await Promise.all([
        readStylistMembers(stylistId),
        readActiveStylists(),
      ]);
      setData({ members: [...members].sort((a, b) => a.name.localeCompare(b.name)), stylists });
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'This stylist’s members could not be loaded.',
      );
    } finally {
      setLoading(false);
    }
  }, [stylistId]);
  useEffect(() => void refresh(), [refresh]);
  const count = data?.members.length ?? 0;
  return (
    <Section
      title="Members"
      description={`The members ${stylistName} looks after. Change a member’s stylist to move them and their open work to someone else.`}
      actions={
        <Button size="sm" color="link-gray" href="/stylist-assignments">
          All stylist assignments
        </Button>
      }
    >
      {notice ? <Callout tone="success" title={notice} /> : null}
      <State loading={loading} {...(error ? { error } : {})} onRetry={() => void refresh()}>
        <MemberAssignmentTable
          label={count === 1 ? 'member' : 'members'}
          members={data?.members ?? []}
          stylists={data?.stylists ?? []}
          onChanged={async (summary) => {
            setNotice(summary);
            await refresh();
            await onChanged?.();
          }}
          emptyHint={`Nobody is assigned to ${stylistName} yet.`}
        />
      </State>
    </Section>
  );
}
